import {
  FC,
  RefObject,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useAppContext } from '@contexts/AppContext';

import { useFetchCurrentUserGagglRank } from '@queries/CritiqueQueries';
import { useCurrentEntertainerQuery } from '@queries/EntertainerQueries';

import { useAnalytics } from '@hooks/useAnalytics';

import { FormLabel } from '@components/form/FormLabel';

import { AuthGateContext, CreateAuthGateHandlerFn } from './AuthGateContext';
import { AuthPopupEntertainer, EntertainerLoginContent } from './EntertainerLoginContent';
import { AuthPopup, AuthPopupProps, useAuthState } from './LogInScreen';
import { UserOnboardingContent } from './UserOnboardingContent';

interface AuthPopupUserOnboarding {
  firstName: string;
  lastName: string;
}

export const AuthGateProvider = ({ children }) => {
  const [authPopupIsVisible, setAuthPopupIsVisible] = useState(false);
  const authState = useAuthState();
  const { userIsLoggedIn } = useAppContext();
  const { entertainer } = useCurrentEntertainerQuery();
  const fetchCurrentUserGagglRank = useFetchCurrentUserGagglRank();
  const { track } = useAnalytics();

  const authCallbackRef = useRef<() => void>(() => {});
  const authCancelledCallbackRef = useRef<() => void>(() => {});
  const onboardingUserRef = useRef<AuthPopupUserOnboarding>(null);
  const entertainerSubmitCallbackRef =
    useRef<(entertainerProperies: AuthPopupEntertainer) => void>(null);
  const optionsRef = useRef<{ cityId?: string }>({});
  const [isEntertainerPopup, setIsEntertainerPopup] = useState(false);
  const [isUserOnboardingPopup, setIsUserOnboardingPopup] = useState(false);
  const [authTitle, setAuthTitle] = useState('');
  const [authMessage, setAuthMessage] = useState('');
  const createAuthGateHandler = useCallback<CreateAuthGateHandlerFn>(
    async (callback, options = {}) => {
      const { authMessage, authTitle, entertainerCallback, onAuthCancelled, ...authOptions } =
        options;

      if (userIsLoggedIn) {
        if ((entertainer && entertainerCallback) || !entertainerCallback) {
          return callback();
        }
      }

      setAuthTitle(authTitle);
      setAuthMessage(authMessage);
      setAuthPopupIsVisible(true);
      authCallbackRef.current = callback;
      entertainerSubmitCallbackRef.current = entertainerCallback;
      authCancelledCallbackRef.current = onAuthCancelled;
      setIsEntertainerPopup(!!entertainerCallback);
      setIsUserOnboardingPopup(!!authOptions.isUserOnboarding);
      optionsRef.current = authOptions;
    },
    [userIsLoggedIn, entertainer?._id],
  );

  const CustomLoginContentComponent = useMemo(() => {
    if (isEntertainerPopup) {
      return EntertainerLoginContent;
    }

    if (isUserOnboardingPopup) {
      return UserOnboardingContent;
    }

    return undefined;
  }, [isEntertainerPopup, isUserOnboardingPopup]);

  return (
    <AuthGateContext.Provider value={{ createAuthGateHandler }}>
      {children}
      <AuthPopup
        isVisible={authPopupIsVisible}
        onClose={() => {
          setAuthPopupIsVisible(false);
          authState.setUserId('');

          const token = localStorage.getItem('token');
          if (!token) {
            requestAnimationFrame(() => {
              track('Authentication Cancelled');
              if (authCancelledCallbackRef.current) {
                track('Authentication Cancelled Callback Triggered');
                authCancelledCallbackRef.current();
              }
            });
          }
        }}
        redirectOverride={async () => {
          await fetchCurrentUserGagglRank();
          requestAnimationFrame(() => {
            authCallbackRef.current?.();
          });
        }}
        entertainerCallback={isEntertainerPopup ? entertainerSubmitCallbackRef.current : undefined}
        setIsEntertainerPopup={setIsEntertainerPopup}
        options={optionsRef.current}
        onboardingUserRef={onboardingUserRef}
        setOnboardingUserProperties={(updatedUserProperties) => {
          onboardingUserRef.current = updatedUserProperties;
        }}
        {...authState}
        authMessage={authMessage}
        authTitle={authTitle}
        loginButtonText="Continue"
        CustomLoginContentComponent={CustomLoginContentComponent}
      />
    </AuthGateContext.Provider>
  );
};
